import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { seoApi } from "@/lib/seoApi";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Sparkles, Plus, Trash2, ChevronUp, ChevronDown, Copy as CopyIcon,
} from "lucide-react";
import SeoResearchPanel from "./SeoResearchPanel";
import TaxonomyPicker from "./TaxonomyPicker";

// Small controlled tag input — comma or Enter commits, chip Backspace removes.
function TagInput({ value, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  function commit(v) {
    const next = (v || "").split(",").map((t) => t.trim()).filter(Boolean);
    if (!next.length) return;
    const set = new Set(value || []);
    for (const t of next) set.add(t);
    onChange([...set]);
    setDraft("");
  }
  function keyDown(e) {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(draft); }
    else if (e.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-1 min-h-[38px] border border-neutral-200 rounded-md px-2 py-1.5 bg-white focus-within:ring-1 focus-within:ring-neutral-400"
         data-testid={testId}>
      {(value || []).map((tag, i) => (
        <span key={`${tag}-${i}`} className="inline-flex items-center gap-1 text-xs bg-neutral-100 text-neutral-800 rounded px-2 py-0.5">
          {tag}
          <button type="button" onClick={() => onChange(value.filter((_, ii) => ii !== i))}
                  className="text-neutral-400 hover:text-neutral-900" aria-label={`remove ${tag}`}>×</button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={keyDown}
        onBlur={() => commit(draft)}
        className="flex-1 min-w-[120px] bg-transparent outline-none text-sm px-1"
        placeholder={placeholder || "Type and press Enter"}
      />
    </div>
  );
}

const SEARCH_INTENTS = [
  { value: "informational", label: "Informational" },
  { value: "commercial", label: "Commercial" },
  { value: "transactional", label: "Transactional" },
  { value: "navigational", label: "Navigational" },
];

// Strip empty buckets / whitespace-only entries before saving.
function _cleanStructuredEntities(se) {
  if (!se || typeof se !== "object") return undefined;
  const out = {};
  for (const [bucket, values] of Object.entries(se)) {
    if (!Array.isArray(values)) continue;
    const vs = values.map((v) => (v || "").trim()).filter(Boolean);
    if (vs.length) out[bucket] = vs;
  }
  return Object.keys(out).length ? out : undefined;
}

const PROVIDERS = [
  { value: "anthropic", label: "Anthropic", defaultModel: "claude-sonnet-4-6" },
  { value: "openai", label: "OpenAI", defaultModel: "gpt-5.4" },
  { value: "gemini", label: "Gemini", defaultModel: "gemini-3.1-pro-preview" },
];

const EMPTY = {
  title: "",
  primary_keyword: "",
  secondary_keywords: [],
  search_intent: "informational",
  country: "",
  language: "English",
  target_audience: "",
  tone_of_voice: "professional",
  min_words: 800,
  max_words: 1500,
  cta: "",
  outline: [{ heading: "", level: 2, key_points: [] }],
  additional_instructions: "",
  provider: "anthropic",
  model: "claude-sonnet-4-6",
  required_terms: [],
  forbidden_terms: [],
  faq_topics: [],
  missing_topics: [],
  nlp_entities: [],
  internal_linking_suggestions: [],
  image_suggestions: [],
  // Universal Taxonomy — every brief classifies into Vertical → Category
  // → Subcategory. Values are Payload document slugs (readable) resolved
  // to IDs at publish time.
  vertical: "",
  category: "",
  subcategory: "",
  primary_topic: "",
  structured_entities: {},
};

// Sticky defaults across briefs — remember last-used values so a user
// working in one market doesn't retype Country/Language/Tone for every
// brief. Cleared only when the user changes them.
const DEFAULTS_KEY = "seo:brief:sticky-defaults";

function loadStickyDefaults() {
  try {
    const raw = window.localStorage.getItem(DEFAULTS_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j || typeof j !== "object") return null;
    return j;
  } catch (_e) {
    return null;
  }
}

function saveStickyDefaults(form) {
  try {
    window.localStorage.setItem(DEFAULTS_KEY, JSON.stringify({
      country: form.country || "",
      language: form.language || "English",
      tone_of_voice: form.tone_of_voice || "professional",
      provider: form.provider || "anthropic",
      model: form.model || "claude-sonnet-4-6",
      search_intent: form.search_intent || "informational",
    }));
  } catch (_e) { /* localStorage disabled */ }
}

function emptyWithStickyDefaults() {
  const sticky = loadStickyDefaults();
  if (!sticky) return EMPTY;
  return {
    ...EMPTY,
    country: sticky.country ?? EMPTY.country,
    language: sticky.language ?? EMPTY.language,
    tone_of_voice: sticky.tone_of_voice ?? EMPTY.tone_of_voice,
    provider: sticky.provider ?? EMPTY.provider,
    model: sticky.model ?? EMPTY.model,
    search_intent: sticky.search_intent ?? EMPTY.search_intent,
  };
}

export default function BriefEditor() {
  const { briefId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const copyFromId = params.get("copy_from");
  const isEdit = Boolean(briefId);
  const draftKey = isEdit ? `seo:brief-draft:edit:${briefId}` : "seo:brief-draft:new";

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit || Boolean(copyFromId));
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [restoredAt, setRestoredAt] = useState(null); // when a draft was restored on mount
  const [duplicated, setDuplicated] = useState(null); // source title if we cloned
  // Snapshot of the initial loaded form so autosave only kicks in on real edits.
  const baselineRef = useRef(null);
  const titleInputRef = useRef(null);

  function seedFromServerBrief(d, { asCopy = false } = {}) {
    const seeded = {
      ...EMPTY,
      ...d,
      // Force title suffix for copies so the operator sees they're editing a new brief
      title: asCopy ? `${d.title || ""} (copy)`.trim() : (d.title || ""),
      secondary_keywords: d.secondary_keywords || [],
      required_terms: d.required_terms || [],
      forbidden_terms: d.forbidden_terms || [],
      outline: (d.outline && d.outline.length)
        ? d.outline.map((s) => ({
            heading: s.heading || "",
            level: s.level || 2,
            intent: s.intent || "",
            min_words: s.min_words || undefined,
            key_points: s.key_points || [],
          }))
        : EMPTY.outline,
      provider: d.provider || "anthropic",
      model: d.model || PROVIDERS[0].defaultModel,
      vertical: d.vertical || "",
      category: d.category || "",
      subcategory: d.subcategory || "",
      primary_topic: d.primary_topic || "",
      structured_entities: (d.structured_entities && typeof d.structured_entities === "object")
        ? d.structured_entities : {},
    };
    return seeded;
  }

  // On mount: load server data (edit or duplicate) OR restore local draft (new).
  useEffect(() => {
    let cancel = false;

    async function loadEdit() {
      try {
        const r = await seoApi.get(`/seo/briefs/${briefId}`);
        if (cancel) return;
        const seeded = seedFromServerBrief(r.data.data || {});
        // For edit-mode, prefer server data. If localStorage has a newer draft
        // (updated_at > server updated_at), offer to restore.
        try {
          const raw = window.localStorage.getItem(draftKey);
          if (raw) {
            const j = JSON.parse(raw);
            const serverUpdated = new Date(r.data.updated_at || 0).getTime();
            if (j && j.savedAt && j.savedAt > serverUpdated + 500 && j.form) {
              setForm(j.form);
              baselineRef.current = j.form;
              setRestoredAt(j.savedAt);
              setLoading(false);
              return;
            }
            // Stale draft — drop it silently
            window.localStorage.removeItem(draftKey);
          }
        } catch (_e) { /* ignore corrupt draft */ }
        setForm(seeded);
        baselineRef.current = seeded;
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    async function loadCopy() {
      try {
        const r = await seoApi.get(`/seo/briefs/${copyFromId}`);
        if (cancel) return;
        const seeded = seedFromServerBrief(r.data.data || {}, { asCopy: true });
        setForm(seeded);
        baselineRef.current = seeded;
        setDuplicated((r.data.data || {}).title || copyFromId.slice(0, 8));
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    function restoreOrEmpty() {
      try {
        const raw = window.localStorage.getItem(draftKey);
        if (raw) {
          const j = JSON.parse(raw);
          if (j && j.form) {
            setForm(j.form);
            baselineRef.current = j.form;
            setRestoredAt(j.savedAt || Date.now());
            setLoading(false);
            return;
          }
        }
      } catch (_e) { /* corrupt draft — nuke it */
        window.localStorage.removeItem(draftKey);
      }
      // Seed with sticky defaults so Country/Tone/Language survive across
      // briefs. Only applies to fresh "new" briefs (edit/duplicate ignore).
      const seed = emptyWithStickyDefaults();
      setForm(seed);
      baselineRef.current = seed;
      setLoading(false);
    }

    if (isEdit) loadEdit();
    else if (copyFromId) loadCopy();
    else restoreOrEmpty();

    return () => { cancel = true; };
  }, [briefId, copyFromId, isEdit]);

  // Autosave draft to localStorage — debounced 500ms. Skips the initial
  // baseline so we don't rewrite draft with a server-loaded snapshot.
  useEffect(() => {
    if (loading) return;
    if (baselineRef.current && form === baselineRef.current) return;
    const t = setTimeout(() => {
      try {
        window.localStorage.setItem(draftKey, JSON.stringify({
          savedAt: Date.now(),
          form,
        }));
      } catch (_e) { /* localStorage disabled / full — silently give up */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form, draftKey, loading]);

  // Autofocus Title on mount for new/duplicate briefs (edit-mode leaves
  // focus alone so the user can jump to whatever they wanted to change).
  useEffect(() => {
    if (loading) return;
    if (isEdit) return;
    const el = titleInputRef.current;
    if (el && typeof el.focus === "function") {
      // Focus without stealing scroll position.
      try { el.focus({ preventScroll: true }); } catch (_e) { el.focus(); }
    }
  }, [loading, isEdit]);

  function clearDraft() {
    try { window.localStorage.removeItem(draftKey); } catch (_e) { /* noop */ }
  }
  function discardDraft() {
    clearDraft();
    setRestoredAt(null);
    if (isEdit) {
      // Reload server copy
      setLoading(true);
      seoApi.get(`/seo/briefs/${briefId}`).then((r) => {
        const seeded = seedFromServerBrief(r.data.data || {});
        setForm(seeded);
        baselineRef.current = seeded;
      }).finally(() => setLoading(false));
    } else if (copyFromId) {
      setLoading(true);
      seoApi.get(`/seo/briefs/${copyFromId}`).then((r) => {
        const seeded = seedFromServerBrief(r.data.data || {}, { asCopy: true });
        setForm(seeded);
        baselineRef.current = seeded;
      }).finally(() => setLoading(false));
    } else {
      const seed = emptyWithStickyDefaults();
      setForm(seed);
      baselineRef.current = seed;
    }
    toast.success("Draft discarded");
  }

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setOutline(idx, patch) {
    setForm((f) => ({
      ...f,
      outline: f.outline.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  }
  function addSection() {
    setForm((f) => ({ ...f, outline: [...f.outline, { heading: "", level: 2, key_points: [] }] }));
  }
  function removeSection(idx) {
    setForm((f) => ({ ...f, outline: f.outline.filter((_, i) => i !== idx) }));
  }
  function moveSection(idx, dir) {
    setForm((f) => {
      const arr = [...f.outline];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, outline: arr };
    });
  }

  const providerObj = useMemo(
    () => PROVIDERS.find((p) => p.value === form.provider) || PROVIDERS[0],
    [form.provider],
  );

  function providerChanged(v) {
    const p = PROVIDERS.find((x) => x.value === v) || PROVIDERS[0];
    setForm((f) => ({ ...f, provider: v, model: p.defaultModel }));
  }

  function buildPayload({ isCreate }) {
    const cleanOutline = (form.outline || [])
      .map((s) => ({
        heading: (s.heading || "").trim(),
        level: [2, 3].includes(Number(s.level)) ? Number(s.level) : 2,
        intent: s.intent || undefined,
        min_words: s.min_words ? Number(s.min_words) : undefined,
        key_points: (s.key_points || []).filter(Boolean),
      }))
      .filter((s) => s.heading);
    const payload = {
      title: form.title.trim(),
      primary_keyword: form.primary_keyword || undefined,
      secondary_keywords: form.secondary_keywords || [],
      search_intent: form.search_intent || undefined,
      country: form.country || undefined,
      language: form.language || undefined,
      target_audience: form.target_audience || undefined,
      tone_of_voice: form.tone_of_voice || undefined,
      min_words: Number(form.min_words) || 800,
      max_words: Number(form.max_words) || 1500,
      cta: form.cta || undefined,
      required_terms: form.required_terms || [],
      forbidden_terms: form.forbidden_terms || [],
      outline: cleanOutline,
      additional_instructions: form.additional_instructions || undefined,
      faq_topics: form.faq_topics || [],
      missing_topics: form.missing_topics || [],
      nlp_entities: form.nlp_entities || [],
      internal_linking_suggestions: form.internal_linking_suggestions || [],
      image_suggestions: form.image_suggestions || [],
      provider: form.provider || undefined,
      model: form.model || undefined,
      vertical: form.vertical || undefined,
      category: form.category || undefined,
      subcategory: form.subcategory || undefined,
      primary_topic: form.primary_topic || undefined,
      structured_entities: _cleanStructuredEntities(form.structured_entities),
      change_reason: isCreate ? "created via brief editor" : "updated via brief editor",
    };
    return payload;
  }

  async function save({ generateAfter }) {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (Number(form.max_words) < Number(form.min_words)) {
      toast.error("Max words must be ≥ Min words");
      return;
    }
    const busySetter = generateAfter ? setGenerating : setSaving;
    busySetter(true);
    try {
      let id = briefId;
      if (isEdit) {
        await seoApi.put(`/seo/briefs/${briefId}`, buildPayload({ isCreate: false }));
      } else {
        const r = await seoApi.post("/seo/briefs", buildPayload({ isCreate: true }));
        id = r.data.id;
      }
      // Remember Country/Language/Tone/Provider so the next new brief
      // doesn't force the user to retype the same market context.
      saveStickyDefaults(form);
      if (generateAfter) {
        toast.info("Generating article — this can take 10–20s…");
        const g = await seoApi.post("/seo/workflows/generate-from-brief", {
          brief_id: id,
          // Brief-level provider/model already saved; pass hints too for
          // clarity so the run row shows the right values immediately.
          provider_hint: form.provider || undefined,
          model_hint: form.model || undefined,
        });
        toast.success("Generation complete");
        clearDraft();
        navigate(`/seo-studio/runs/${g.data.id}`);
      } else {
        toast.success(isEdit ? "Brief updated" : "Brief created");
        clearDraft();
        navigate(`/seo-studio/briefs/${id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      busySetter(false);
    }
  }

  if (loading) return <div className="text-neutral-500">Loading…</div>;

  function onKeyDown(e) {
    // Cmd/Ctrl + Enter → Save & Generate. Works from any field in the form.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!saving && !generating) save({ generateAfter: true });
    }
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="brief-editor" onKeyDown={onKeyDown}>
      <Link to={isEdit ? `/seo-studio/briefs/${briefId}` : "/seo-studio/briefs"}
            className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> {isEdit ? "Back to brief" : "Briefs"}
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {isEdit ? "Edit Brief" : (duplicated ? "Duplicate Brief" : "New Brief")}
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            Fill in the details, then Save &amp; Generate to send the article straight to the LLM.
          </p>
        </div>
      </div>

      {duplicated && (
        <div className="flex items-center gap-3 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
             data-testid="duplicated-banner">
          <CopyIcon size={14} />
          Cloned from <span className="font-medium">{duplicated}</span>. Adjust the fields, then Save &amp; Generate.
        </div>
      )}

      {restoredAt && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
             data-testid="draft-restored-banner">
          <div>
            <span className="font-medium">Draft restored</span> — last edit {formatAgo(restoredAt)}. Your changes are saved locally as you type.
          </div>
          <button type="button" onClick={discardDraft}
                  className="text-xs underline hover:no-underline"
                  data-testid="discard-draft-btn">
            Discard local draft
          </button>
        </div>
      )}

      {/* Basics */}
      <Card>
        <CardHeader><CardTitle className="text-base">Basics</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Field label="Title" required>
            <Input ref={titleInputRef} value={form.title} onChange={(e) => set("title", e.target.value)}
                   placeholder="E.g. Sourcing Vietnamese Oolong Tea for Boutique Retailers"
                   data-testid="brief-title" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Primary keyword">
              <Input value={form.primary_keyword}
                     onChange={(e) => set("primary_keyword", e.target.value)}
                     placeholder="vietnam oolong tea sourcing"
                     data-testid="brief-primary-keyword" />
            </Field>
            <Field label="Search intent">
              <Select value={form.search_intent} onChange={(v) => set("search_intent", v)}
                      options={SEARCH_INTENTS} testId="brief-search-intent" />
            </Field>
          </div>
          <Field label="Secondary keywords" hint="Comma or Enter to add">
            <TagInput value={form.secondary_keywords}
                      onChange={(v) => set("secondary_keywords", v)}
                      placeholder="oolong, green tea, moq…"
                      testId="brief-secondary-keywords" />
          </Field>

          <SeoResearchPanel
            primaryKeyword={form.primary_keyword}
            secondaryKeywords={form.secondary_keywords}
            country={form.country}
            language={form.language}
            targetAudience={form.target_audience}
            onBriefGenerated={(brief, meta) => {
              // Full auto-fill from Claude 4.6. Merge over current form so
              // any manual edits already made are preserved when the LLM
              // returned an empty field. Only replace when LLM produced a value.
              setForm((f) => ({
                ...f,
                title: brief.title || f.title,
                primary_keyword: brief.primary_keyword || f.primary_keyword,
                secondary_keywords: brief.secondary_keywords?.length
                  ? brief.secondary_keywords : f.secondary_keywords,
                search_intent: brief.search_intent || f.search_intent,
                country: brief.country || f.country,
                language: brief.language || f.language,
                target_audience: brief.target_audience || f.target_audience,
                tone_of_voice: brief.tone_of_voice || f.tone_of_voice,
                min_words: brief.min_words || f.min_words,
                max_words: brief.max_words || f.max_words,
                cta: brief.cta || f.cta,
                outline: (brief.outline || []).length ? brief.outline : f.outline,
                additional_instructions: brief.additional_instructions || f.additional_instructions,
                faq_topics: brief.faq_topics || [],
                missing_topics: brief.missing_topics || [],
                nlp_entities: brief.nlp_entities || [],
                internal_linking_suggestions: brief.internal_linking_suggestions || [],
                image_suggestions: brief.image_suggestions || [],
              }));
              // Scroll to top so user reviews the auto-filled title first
              try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_e) { /* noop */ }
            }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Country">
              <Input value={form.country} onChange={(e) => set("country", e.target.value)}
                     placeholder="United States"
                     data-testid="brief-country" />
            </Field>
            <Field label="Language">
              <Input value={form.language} onChange={(e) => set("language", e.target.value)}
                     placeholder="English"
                     data-testid="brief-language" />
            </Field>
          </div>
          <Field label="Target audience">
            <Textarea rows={2} value={form.target_audience}
                      onChange={(e) => set("target_audience", e.target.value)}
                      placeholder="Small e-commerce owners importing specialty tea…"
                      data-testid="brief-audience" />
          </Field>
        </CardContent>
      </Card>

      {/* Taxonomy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Taxonomy</CardTitle>
        </CardHeader>
        <CardContent>
          <TaxonomyPicker form={form} setForm={setForm} />
        </CardContent>
      </Card>

      {/* Voice & Length */}
      <Card>
        <CardHeader><CardTitle className="text-base">Voice & Length</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Tone of voice">
              <Input value={form.tone_of_voice} onChange={(e) => set("tone_of_voice", e.target.value)}
                     placeholder="professional / friendly / expert"
                     data-testid="brief-tone" />
            </Field>
            <Field label="Min words">
              <Input type="number" value={form.min_words}
                     onChange={(e) => set("min_words", e.target.value)}
                     data-testid="brief-min-words" />
            </Field>
            <Field label="Max words">
              <Input type="number" value={form.max_words}
                     onChange={(e) => set("max_words", e.target.value)}
                     data-testid="brief-max-words" />
            </Field>
          </div>
          <Field label="Call to action">
            <Textarea rows={2} value={form.cta} onChange={(e) => set("cta", e.target.value)}
                      placeholder="Contact us for a sourcing consultation."
                      data-testid="brief-cta" />
          </Field>
        </CardContent>
      </Card>

      {/* Outline */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Outline (H2 / H3)</CardTitle>
          <Button size="sm" variant="outline" onClick={addSection} data-testid="brief-add-section">
            <Plus size={14} /> Add section
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.outline.length === 0 && (
            <p className="text-sm text-neutral-500">No sections yet — add at least one.</p>
          )}
          {form.outline.map((s, idx) => (
            <div key={idx} className="border border-neutral-200 rounded-md p-3 bg-white space-y-2"
                 data-testid={`brief-section-${idx}`}>
              <div className="flex items-center gap-2">
                <select
                  value={s.level}
                  onChange={(e) => setOutline(idx, { level: Number(e.target.value) })}
                  className="text-xs border border-neutral-200 rounded px-2 py-1.5 bg-white"
                  data-testid={`brief-section-${idx}-level`}
                >
                  <option value={2}>H2</option>
                  <option value={3}>H3</option>
                </select>
                <Input
                  value={s.heading}
                  onChange={(e) => setOutline(idx, { heading: e.target.value })}
                  placeholder="Section heading"
                  className="flex-1"
                  data-testid={`brief-section-${idx}-heading`}
                />
                <button type="button" onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                        className="p-1.5 text-neutral-400 hover:text-neutral-900 disabled:opacity-30"
                        aria-label="move up">
                  <ChevronUp size={14} />
                </button>
                <button type="button" onClick={() => moveSection(idx, +1)}
                        disabled={idx === form.outline.length - 1}
                        className="p-1.5 text-neutral-400 hover:text-neutral-900 disabled:opacity-30"
                        aria-label="move down">
                  <ChevronDown size={14} />
                </button>
                <button type="button" onClick={() => removeSection(idx)}
                        className="p-1.5 text-neutral-400 hover:text-rose-600"
                        aria-label="remove section"
                        data-testid={`brief-section-${idx}-remove`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <TagInput
                value={s.key_points}
                onChange={(v) => setOutline(idx, { key_points: v })}
                placeholder="Key point, key point…"
                testId={`brief-section-${idx}-keypoints`}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={s.intent || ""}
                  onChange={(e) => setOutline(idx, { intent: e.target.value })}
                  placeholder="Intent (optional)"
                  className="text-xs"
                />
                <Input
                  type="number"
                  value={s.min_words || ""}
                  onChange={(e) => setOutline(idx, { min_words: e.target.value })}
                  placeholder="Min words (optional)"
                  className="text-xs"
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* AI Setup */}
      <Card>
        <CardHeader><CardTitle className="text-base">AI Setup</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="LLM provider">
            <Select value={form.provider} onChange={providerChanged}
                    options={PROVIDERS} testId="brief-provider" />
          </Field>
          <Field label="Model" hint={`Default for ${providerObj.label}: ${providerObj.defaultModel}`}>
            <Input value={form.model} onChange={(e) => set("model", e.target.value)}
                   placeholder={providerObj.defaultModel}
                   data-testid="brief-model" />
          </Field>
        </CardContent>
      </Card>

      {/* Auto-brief insights — from Phase D research-driven Brief generation. Only rendered when populated. */}
      {(form.faq_topics?.length || form.missing_topics?.length || form.nlp_entities?.length
        || form.internal_linking_suggestions?.length || form.image_suggestions?.length) ? (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="brief-auto-insights">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-700" />
              Auto-brief research insights
              <span className="text-[10px] font-normal text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded-sm">
                will be woven into the article
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.faq_topics?.length > 0 && (
              <Field label={`FAQ topics (${form.faq_topics.length})`} hint="Article will include these as FAQ questions">
                <TagInput value={form.faq_topics}
                          onChange={(v) => set("faq_topics", v)}
                          placeholder="Add FAQ topic…"
                          testId="brief-faq-topics" />
              </Field>
            )}
            {form.missing_topics?.length > 0 && (
              <Field label={`Missing topics (competitor gaps) (${form.missing_topics.length})`}
                     hint="Angles competitors do NOT cover — your differentiation edge">
                <TagInput value={form.missing_topics}
                          onChange={(v) => set("missing_topics", v)}
                          placeholder="Add differentiation angle…"
                          testId="brief-missing-topics" />
              </Field>
            )}
            {form.nlp_entities?.length > 0 && (
              <Field label={`NLP entities (${form.nlp_entities.length})`}
                     hint="Named entities LLM will mention where relevant">
                <TagInput value={form.nlp_entities}
                          onChange={(v) => set("nlp_entities", v)}
                          placeholder="Add entity…"
                          testId="brief-nlp-entities" />
              </Field>
            )}
            {form.internal_linking_suggestions?.length > 0 && (
              <Field label={`Internal linking suggestions (${form.internal_linking_suggestions.length})`}
                     hint="Anchor topics to link internally when related articles exist">
                <TagInput value={form.internal_linking_suggestions}
                          onChange={(v) => set("internal_linking_suggestions", v)}
                          placeholder="Add anchor topic…"
                          testId="brief-internal-links" />
              </Field>
            )}
            {form.image_suggestions?.length > 0 && (
              <Field label={`Image suggestions (${form.image_suggestions.length})`}
                     hint="Concrete photographable subjects — LLM will emit [[BLOCK:IMAGE]] markers where they fit">
                <TagInput value={form.image_suggestions}
                          onChange={(v) => set("image_suggestions", v)}
                          placeholder="Add image subject…"
                          testId="brief-image-suggestions" />
              </Field>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Additional instructions & terms */}
      <Card>
        <CardHeader><CardTitle className="text-base">Additional instructions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Field label="Free-form instructions">
            <Textarea rows={4} value={form.additional_instructions}
                      onChange={(e) => set("additional_instructions", e.target.value)}
                      placeholder="Include a comparison table. Avoid superlatives. …"
                      data-testid="brief-instructions" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Required terms">
              <TagInput value={form.required_terms}
                        onChange={(v) => set("required_terms", v)}
                        placeholder="Vietnam, quality…"
                        testId="brief-required-terms" />
            </Field>
            <Field label="Forbidden terms">
              <TagInput value={form.forbidden_terms}
                        onChange={(v) => set("forbidden_terms", v)}
                        placeholder="cheap, spammy…"
                        testId="brief-forbidden-terms" />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Action bar */}
      <div className="sticky bottom-0 bg-neutral-50/95 backdrop-blur-sm border-t border-neutral-200 -mx-6 px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div className="text-xs text-neutral-500 flex items-center gap-2">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
          Autosaved locally · <kbd className="px-1 py-0.5 bg-neutral-200 text-neutral-700 rounded text-[10px] font-mono">⌘ Enter</kbd> to Save &amp; Generate
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => save({ generateAfter: false })}
                  disabled={saving || generating}
                  data-testid="brief-save">
            <Save size={14} /> {saving ? "Saving…" : "Save only"}
          </Button>
          <Button onClick={() => save({ generateAfter: true })}
                  disabled={saving || generating}
                  size="lg"
                  className="bg-neutral-900 text-white hover:bg-neutral-800 shadow-md"
                  data-testid="brief-save-and-generate">
            <Sparkles size={16} className="mr-1" />
            {generating ? "Generating…" : "Save & Generate"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="flex items-center gap-1">
        {label}
        {required && <span className="text-rose-500">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-neutral-200 rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400"
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function formatAgo(ts) {
  const secs = Math.max(1, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(ts).toLocaleString();
}
